import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as ClientApi from '~/lib/ClientApi';
import emitter, { EmitterEventTypes } from '~/lib/emitter';
import {
    CustomQuestionMode,
    type CustomQuestionClientHistory,
    type CustomQuestionModeName,
} from './CustomQuestionApi';
import { sampleClientHistory } from './sampleData';

/** Long enough that several questions on one form share a request */
const HISTORY_STALE_MS = 30 * 1000;

/**
 * How breely.getClientHistory() is answered for one custom question.
 *
 * On a form a client is using, it is the logged in client's history from
 *  /account/custom-question-history - built for custom questions, so none of
 *  the tokens in the account's own /account/history ever reach the frame.
 *  Nobody logged in is an answer, null, rather than an error.
 *
 * An admin reading a submission is not the client, so there it isn't offered
 *  at all and the frame is told not-available. In preview the question gets
 *  sample history.
 */
export default function useClientHistoryActions({
    mode,
}: {
    mode: CustomQuestionModeName;
}) {
    const queryClient = useQueryClient();
    const [clientToken, setClientToken] = useState(() => ClientApi.getToken());

    // Logging in happens part way through a form, ex. on a client account
    //  question, and the frame is told so it can ask again
    useEffect(() => {
        const onTokenUpdated = () => setClientToken(ClientApi.getToken());

        emitter.on(EmitterEventTypes.CLIENT_TOKEN_UPDATED, onTokenUpdated);

        return () =>
            emitter.off(EmitterEventTypes.CLIENT_TOKEN_UPDATED, onTokenUpdated);
    }, []);

    const isPreview = mode === CustomQuestionMode.PREVIEW;
    const isClientFacing =
        mode === CustomQuestionMode.FILL ||
        mode === CustomQuestionMode.READONLY;

    const getClientHistory =
        useCallback((): Promise<CustomQuestionClientHistory | null> => {
            if (isPreview) {
                return Promise.resolve(sampleClientHistory());
            }

            // Read now rather than from state, in case the question asks in
            //  the same moment the client logs in
            const token = ClientApi.getToken();

            if (!token) {
                return Promise.resolve(null);
            }

            return queryClient
                .fetchQuery({
                    queryKey: ['custom-question-history', token],
                    queryFn: () =>
                        ClientApi.get('/account/custom-question-history'),
                    staleTime: HISTORY_STALE_MS,
                })
                .catch(err => {
                    // An expired login is the same as no login
                    if (err?.response?.status === 401) {
                        return null;
                    }

                    throw new Error(
                        err?.response?.data?.message ||
                            'The client history could not be loaded',
                    );
                });
        }, [isPreview, queryClient]);

    return {
        onGetClientHistory:
            isPreview || isClientFacing ? getClientHistory : undefined,
        clientKey: isClientFacing ? clientToken : null,
    };
}
